import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

export interface ClientUser {
  id: number;
  client_id: number;
  email: string;
  name: string;
  preferred_language: string | null;
  has_password: boolean;
  client_name: string;
  unread_notifications: number;
  // Populated by the API (camelCase). Optional so older responses stay valid.
  emailVerified?: boolean;
  hasGoogle?: boolean;
}

interface ClientAuthContextType {
  user: ClientUser | null;
  loading: boolean;
  login: (email: string, password: string, turnstileToken?: string) => Promise<void>;
  register: (data: { email: string; password: string; name?: string; turnstileToken?: string; lang?: string }) => Promise<void>;
  requestMagicLink: (email: string, portalToken?: string, turnstileToken?: string) => Promise<{ message: string }>;
  verifyMagicLink: (token: string, lang?: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  resendVerification: () => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (data: { name?: string; password?: string; preferred_language?: string }) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const ClientAuthContext = createContext<ClientAuthContextType | null>(null);

const API = '/api/portal';

async function clientRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      ...options.headers,
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    },
    credentials: 'same-origin',
  });

  const data = await res.json() as T & { error?: string };

  if (!res.ok) {
    throw new Error(data.error || 'Error desconocido');
  }

  return data;
}

export function ClientAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ClientUser | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const data = await clientRequest<{ user: ClientUser }>('/auth/me');
      setUser(data.user);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    fetchMe().finally(() => setLoading(false));
  }, [fetchMe]);

  const login = useCallback(async (email: string, password: string, turnstileToken?: string) => {
    const data = await clientRequest<{ user: ClientUser }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, turnstileToken }),
    });
    setUser(data.user);
  }, []);

  const register = useCallback(async (data: { email: string; password: string; name?: string; turnstileToken?: string; lang?: string }) => {
    const result = await clientRequest<{ user: ClientUser }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    setUser(result.user); // signup logs the user in immediately
  }, []);

  const requestMagicLink = useCallback(async (email: string, portalToken?: string, turnstileToken?: string) => {
    return clientRequest<{ message: string }>('/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify({ email, portalToken, turnstileToken }),
    });
  }, []);

  const verifyMagicLink = useCallback(async (token: string, lang?: string) => {
    const data = await clientRequest<{ user: ClientUser }>('/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ token, lang }),
    });
    setUser(data.user);
  }, []);

  // Confirm an email via the token from the verification link, then refresh
  // the session user so the banner disappears.
  const verifyEmail = useCallback(async (token: string) => {
    await clientRequest('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
    await fetchMe();
  }, [fetchMe]);

  const resendVerification = useCallback(async () => {
    await clientRequest('/auth/resend-verification', { method: 'POST' });
  }, []);

  const logout = useCallback(async () => {
    await clientRequest('/auth/logout', { method: 'POST' });
    setUser(null);
  }, []);

  const updateUser = useCallback(async (data: { name?: string; password?: string; preferred_language?: string }) => {
    const result = await clientRequest<{ user: ClientUser }>('/auth/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    setUser(result.user);
  }, []);

  return (
    <ClientAuthContext.Provider value={{ user, loading, login, register, requestMagicLink, verifyMagicLink, verifyEmail, resendVerification, logout, updateUser, refreshUser: fetchMe }}>
      {children}
    </ClientAuthContext.Provider>
  );
}

export function useClientAuth(): ClientAuthContextType {
  const context = useContext(ClientAuthContext);
  if (!context) throw new Error('useClientAuth must be used within ClientAuthProvider');
  return context;
}
